import { useEffect, useState } from "react";

// ---------- Livelli ----------
export const xpForLevel = (l) => { const n = l - 1; return 100 * n + 25 * n * n; };
export const levelInfo = (xp = 0) => {
  let level = 1;
  while (level < 99 && xp >= xpForLevel(level + 1)) level++;
  const cur = xpForLevel(level), next = xpForLevel(level + 1);
  return { level, xp, cur, next, progress: Math.round(((xp - cur) / (next - cur)) * 100) };
};
const LEVEL_TITLES = ["Esploratore", "Apprendista", "Campione", "Esperto", "Maestro", "Leggenda"];
export const levelTitle = (level) => LEVEL_TITLES[Math.min(LEVEL_TITLES.length - 1, Math.floor((level - 1) / 5))];

export function LevelBar({ child, lang = "it", compact = false }) {
  const li = levelInfo(child?.xp || 0);
  return (
    <div className={`bg-gradient-to-r from-indigo-600 to-violet-600 rounded-2xl text-white ${compact ? "p-2" : "p-3"} shadow-lg`} data-testid="level-bar">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-full bg-white/20 border-2 border-amber-300 flex flex-col items-center justify-center leading-none">
          <span className="text-[9px] uppercase text-amber-200">{lang === "it" ? "Liv" : "Lvl"}</span>
          <span className="font-black text-lg" data-testid="level-number">{li.level}</span>
        </div>
        <div className="flex-1">
          <div className="flex justify-between text-xs mb-1">
            <span className="font-bold">{levelTitle(li.level)}</span>
            <span className="text-white/80" data-testid="level-xp">{li.xp - li.cur}/{li.next - li.cur} XP</span>
          </div>
          <div className="h-3 bg-black/30 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-amber-300 to-yellow-400 transition-all duration-700" style={{ width: `${Math.max(3, li.progress)}%` }} />
          </div>
          {!compact && <p className="text-[11px] text-white/70 mt-1">{lang === "it" ? `Mancano ${li.next - li.xp} XP al livello ${li.level + 1}` : `${li.next - li.xp} XP to level ${li.level + 1}`}</p>}
        </div>
      </div>
    </div>
  );
}

export function LevelUpOverlay({ level, bonus, onClose, lang = "it" }) {
  useEffect(() => { const t = setTimeout(onClose, 3500); return () => clearTimeout(t); }, [onClose]);
  return (
    <div className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="level-up-overlay">
      <div className="text-center animate-bounce">
        <div className="text-7xl mb-2">⬆️</div>
        <div className="text-5xl font-black text-amber-300 drop-shadow-lg">LEVEL UP!</div>
        <div className="text-white text-2xl font-bold mt-2">{lang === "it" ? "Livello" : "Level"} {level} · {levelTitle(level)}</div>
        {bonus > 0 && <div className="text-yellow-300 font-bold text-xl mt-2" data-testid="level-up-bonus">+{bonus} 💰 bonus</div>}
      </div>
    </div>
  );
}

// ---------- Combo ----------
export function ComboPopup({ combo }) {
  if (!combo || combo < 2) return null;
  const mult = combo >= 10 ? "x5" : combo >= 5 ? "x3" : combo >= 3 ? "x2" : "";
  return (
    <div className="pointer-events-none fixed inset-x-0 top-24 flex justify-center z-40" data-testid="combo-popup">
      <div className={`px-5 py-2 rounded-full font-black text-2xl shadow-xl animate-bounce ${combo >= 5 ? "bg-red-500 text-white" : "bg-amber-400 text-gray-900"}`}>
        🔥 COMBO {combo} {mult && <span className="text-sm align-top">{mult}</span>}
      </div>
    </div>
  );
}

// ---------- Riepilogo ricompense (usato nella schermata risultati) ----------
export function RewardBreakdown({ res, lang = "it" }) {
  if (!res || res.xp_earned === undefined) return null;
  const it = lang === "it";
  const li = res.level;
  return (
    <div className="space-y-2 mb-4 text-left" data-testid="reward-breakdown">
      {res.new_record && (
        <div className="bg-gradient-to-r from-amber-400 to-yellow-300 rounded-xl p-3 font-black text-gray-900 text-center animate-pulse" data-testid="new-record-banner">
          🏆 {it ? "NUOVO RECORD PERSONALE!" : "NEW PERSONAL RECORD!"} {res.record_value !== undefined && <span className="text-sm font-bold">({res.record_value})</span>}
        </div>
      )}
      {res.rival && (
        <div className={`rounded-xl p-3 flex items-center gap-3 ${res.rival.beaten ? "bg-green-100 border-2 border-green-300" : "bg-gray-100 border-2 border-gray-200"}`} data-testid="rival-result">
          <span className="text-3xl">{res.rival.avatar}</span>
          <div className="flex-1 text-sm">
            <div className="font-bold text-gray-800">{it ? "Rivale del giorno" : "Rival of the day"}: {res.rival.name} — {res.rival.target}/{res.rival.total}</div>
            <div className={res.rival.beaten ? "text-green-700 font-bold" : "text-gray-600"}>
              {res.rival.beaten ? (it ? `Battuto! +${res.rival_bonus} 💰` : `Beaten! +${res.rival_bonus} 💰`) : (it ? "Non ancora battuto… riprova!" : "Not beaten yet… try again!")}
            </div>
          </div>
        </div>
      )}
      {res.combo_bonus > 0 && (
        <div className="bg-orange-100 border-2 border-orange-300 rounded-xl p-2 text-sm font-bold text-orange-700 text-center" data-testid="combo-bonus">
          🔥 {it ? "Combo massima" : "Max combo"} {res.max_combo} → +{res.combo_bonus} 💰
        </div>
      )}
      {li && (
        <div className="bg-indigo-50 rounded-xl p-3" data-testid="xp-gain">
          <div className="flex justify-between text-sm font-bold text-indigo-700"><span>+{res.xp_earned} XP</span><span>{it ? "Livello" : "Level"} {li.level}</span></div>
          <div className="h-2 bg-indigo-100 rounded-full overflow-hidden mt-1"><div className="h-full bg-indigo-500 transition-all duration-700" style={{ width: `${Math.max(3, li.progress)}%` }} /></div>
        </div>
      )}
    </div>
  );
}

// ---------- Rivale del giorno (prima della partita / home) ----------
export function RivalCard({ rival, lang = "it", record }) {
  if (!rival) return null;
  const it = lang === "it";
  return (
    <div className="bg-white/90 rounded-2xl p-3 flex items-center gap-3 shadow" data-testid="rival-card">
      <span className="text-3xl">{rival.avatar}</span>
      <div className="flex-1 text-sm">
        <div className="font-bold text-gray-800">🎯 {it ? "Batti" : "Beat"} {rival.name}: {rival.target}/{rival.total}</div>
        <div className="text-xs text-gray-500">{it ? "+15 monete se lo superi" : "+15 coins if you beat it"}{record !== undefined && record !== null ? ` · ${it ? "Tuo record" : "Your best"}: ${record}` : ""}</div>
      </div>
    </div>
  );
}

// ---------- Classifica settimanale widget ----------
export function WeeklyRankWidget({ rank, lang = "it", onOpen }) {
  if (!rank) return null;
  const it = lang === "it";
  const days = Math.max(0, Math.ceil(rank.seconds_left / 86400));
  return (
    <button onClick={onOpen} className="w-full bg-gradient-to-r from-rose-500 to-orange-500 rounded-2xl p-3 text-white text-left shadow-lg hover:opacity-95" data-testid="weekly-rank-widget">
      <div className="flex items-center gap-3">
        <div className="text-3xl">🏅</div>
        <div className="flex-1">
          <div className="font-black text-lg" data-testid="weekly-rank-text">
            {rank.points === 0 ? (it ? "Entra in classifica questa settimana!" : "Join this week's leaderboard!") : rank.rank === 1 ? (it ? "Sei 1°! Difendi il trono 👑" : "You're #1! Defend the throne 👑") : (it ? `Sei ${rank.rank}° con ${rank.points} punti` : `You're #${rank.rank} with ${rank.points} pts`)}
          </div>
          <div className="text-xs text-white/85">
            {rank.next ? (it ? `Ti mancano ${rank.next.gap} punti per superare ${rank.next.avatar} ${rank.next.name}` : `${rank.next.gap} pts to pass ${rank.next.avatar} ${rank.next.name}`) : (it ? "Ogni moneta vinta vale 1 punto" : "Every coin earned = 1 point")}
            {" · "}{it ? `${days}g alla fine` : `${days}d left`} · 🥇100 🥈60 🥉30
          </div>
        </div>
        <span className="text-white/70">→</span>
      </div>
    </button>
  );
}

// ---------- Calendario streak 7 giorni ----------
export function StreakCalendar({ streak = 0, lang = "it" }) {
  const filled = Math.min(streak, 7);
  return (
    <div className="flex items-center gap-1" data-testid="streak-calendar" title={lang === "it" ? `${streak} giorni di fila` : `${streak}-day streak`}>
      {Array.from({ length: 7 }, (_, i) => (
        <span key={i} className={`w-6 h-6 rounded-full text-xs flex items-center justify-center font-bold ${i < filled ? "bg-orange-500 text-white" : "bg-gray-200 text-gray-400"}`}>{i < filled ? "🔥" : i + 1}</span>
      ))}
    </div>
  );
}

// ---------- Scrigno giornaliero ----------
export function DailyChestModal({ onOpen, onClose, streak = 0, lang = "it", playSound }) {
  const [state, setState] = useState("closed"); // closed | opening | opened
  const [prize, setPrize] = useState(null);
  const it = lang === "it";
  const open = async () => {
    setState("opening");
    if (playSound) playSound("click");
    const p = await onOpen();
    setTimeout(() => {
      if (!p) { onClose(); return; }
      setPrize(p); setState("opened");
      if (playSound) playSound(p.rarity === "epic" ? "levelup" : "coins");
    }, 900);
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" data-testid="daily-chest-modal">
      <div className="bg-gradient-to-b from-amber-50 to-orange-100 rounded-3xl p-6 max-w-sm w-full text-center shadow-2xl">
        <h3 className="text-2xl font-black text-amber-800 mb-1">🎁 {it ? "Scrigno del giorno" : "Daily chest"}</h3>
        <p className="text-sm text-amber-700 mb-3">{it ? "Torna ogni giorno per un premio a sorpresa!" : "Come back every day for a surprise!"}</p>
        <div className="flex justify-center mb-3"><StreakCalendar streak={streak} lang={lang} /></div>
        <div className={`text-8xl my-4 ${state === "opening" ? "animate-bounce" : ""}`}>{state === "opened" ? "🎉" : "🧰"}</div>
        {state === "opened" && prize ? (
          <div data-testid="daily-chest-prize">
            <div className={`text-4xl font-black ${prize.rarity === "epic" ? "text-fuchsia-600" : prize.rarity === "rare" ? "text-blue-600" : "text-amber-700"}`}>+{prize.coins} 💰</div>
            <div className="text-xs text-gray-600 mt-1">
              {prize.rarity === "epic" ? (it ? "✨ PREMIO EPICO!" : "✨ EPIC PRIZE!") : prize.rarity === "rare" ? (it ? "💎 Premio raro!" : "💎 Rare prize!") : (it ? "Bel colpo!" : "Nice!")}
              {prize.streak_bonus > 0 && ` · 🔥 +${prize.streak_bonus} ${it ? "bonus serie" : "streak bonus"}`}
            </div>
            <button onClick={onClose} className="mt-4 w-full bg-amber-600 text-white py-3 rounded-xl font-bold" data-testid="daily-chest-close-btn">{it ? "Fantastico!" : "Awesome!"}</button>
          </div>
        ) : (
          <>
            <button onClick={open} disabled={state === "opening"} className="w-full bg-gradient-to-r from-amber-500 to-orange-600 text-white py-3 rounded-xl font-black text-lg disabled:opacity-60" data-testid="daily-chest-open-btn">
              {state === "opening" ? (it ? "Apertura…" : "Opening…") : (it ? "Apri lo scrigno!" : "Open the chest!")}
            </button>
            <button onClick={onClose} className="mt-2 text-sm text-amber-700 underline" data-testid="daily-chest-later-btn">{it ? "Più tardi" : "Later"}</button>
          </>
        )}
      </div>
    </div>
  );
}

// ---------- Ruota della fortuna ----------
const WHEEL_COLORS = ["#f59e0b", "#ef4444", "#10b981", "#6b7280", "#8b5cf6", "#3b82f6", "#ec4899", "#facc15"];
export function FortuneWheel({ segments, spinsLeft, onSpin, lang = "it", playSound }) {
  const [angle, setAngle] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);
  const [left, setLeft] = useState(spinsLeft);
  const it = lang === "it";
  const n = segments.length, seg = 360 / n;
  const spin = async () => {
    if (spinning || left <= 0) return;
    setSpinning(true); setResult(null);
    const r = await onSpin();
    if (!r) { setSpinning(false); return; }
    const target = 360 * 5 + (360 - (r.index * seg + seg / 2));
    setAngle((a) => a + target - (a % 360));
    setTimeout(() => {
      setSpinning(false); setResult(r); setLeft(r.spins_left);
      if (playSound) playSound(r.coins >= 50 ? "levelup" : r.coins > 0 ? "coins" : "click");
    }, 3200);
  };
  const gradient = segments.map((_, i) => `${WHEEL_COLORS[i % WHEEL_COLORS.length]} ${i * seg}deg ${(i + 1) * seg}deg`).join(", ");
  return (
    <div className="bg-gradient-to-b from-violet-50 to-fuchsia-50 border-2 border-fuchsia-200 rounded-2xl p-4 mb-4" data-testid="fortune-wheel">
      <div className="font-black text-fuchsia-700 mb-2">🎡 {it ? "Ruota della Fortuna" : "Fortune Wheel"} <span className="text-xs font-bold text-gray-500">({left} {it ? "giri oggi" : "spins today"})</span></div>
      <div className="relative w-44 h-44 mx-auto">
        <div className="absolute -top-2 left-1/2 -translate-x-1/2 text-2xl z-10">🔻</div>
        <div className="w-44 h-44 rounded-full border-4 border-white shadow-xl" style={{ background: `conic-gradient(${gradient})`, transform: `rotate(${angle}deg)`, transition: spinning ? "transform 3.2s cubic-bezier(0.2, 0.8, 0.2, 1)" : "none" }}>
          {segments.map((s, i) => (
            <div key={i} className="absolute left-1/2 top-1/2 text-white font-black text-xs drop-shadow" style={{ transform: `rotate(${i * seg + seg / 2}deg) translateY(-62px) translateX(-50%)` }}>{s}</div>
          ))}
        </div>
      </div>
      {result ? (
        <div className="text-center mt-3 font-black text-xl text-fuchsia-700" data-testid="wheel-result">{result.coins > 0 ? `+${result.coins} 💰` : (it ? "Ritenta!" : "Try again!")}</div>
      ) : null}
      <button onClick={spin} disabled={spinning || left <= 0} className="w-full mt-3 bg-gradient-to-r from-fuchsia-600 to-violet-600 text-white py-2.5 rounded-xl font-bold disabled:opacity-50" data-testid="wheel-spin-btn">
        {spinning ? (it ? "Gira…" : "Spinning…") : left <= 0 ? (it ? "Torna domani!" : "Come back tomorrow!") : (it ? "GIRA!" : "SPIN!")}
      </button>
    </div>
  );
}

// ---------- Nudge missione quasi completata ----------
export function MissionNudge({ missions = [], lang = "it", onOpen }) {
  const near = missions.find((m) => !m.claimed && m.progress > 0 && m.progress < m.target && m.target - m.progress <= 1);
  const ready = missions.find((m) => !m.claimed && m.progress >= m.target);
  const it = lang === "it";
  if (!near && !ready) return null;
  return (
    <button onClick={onOpen} className="w-full bg-emerald-500 text-white rounded-2xl p-3 text-left font-bold shadow-lg flex items-center gap-3 hover:opacity-95" data-testid="mission-nudge">
      <span className="text-2xl">🎯</span>
      <span className="text-sm">
        {ready ? (it ? `Missione completata: ritira ${ready.reward} monete!` : `Mission done: claim ${ready.reward} coins!`) : (it ? `Ti manca solo 1 per completare "${near.text_it}" (+${near.reward} 💰)` : `Just 1 more to finish "${near.text_en}" (+${near.reward} 💰)`)}
      </span>
    </button>
  );
}

// ---------- Timer Blitz ----------
export function BlitzTimer({ timeLeft, total = 60, correct = 0 }) {
  const pct = (timeLeft / total) * 100;
  return (
    <div className="mb-4" data-testid="blitz-timer">
      <div className="flex justify-between text-white font-black mb-1">
        <span className={timeLeft <= 10 ? "text-red-300 animate-pulse" : ""}>⚡ {timeLeft}s</span>
        <span data-testid="blitz-correct">✅ {correct}</span>
      </div>
      <div className="h-4 bg-black/30 rounded-full overflow-hidden">
        <div className={`h-full transition-all duration-1000 ${timeLeft <= 10 ? "bg-red-500" : "bg-gradient-to-r from-lime-400 to-emerald-400"}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
